import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  InfiniteData,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { env } from '@env';

import customInstance, { ErrorType } from '../mutator/custom-instance';
import { ListYtChannelsParams, YtChannelArray } from './model';

/**
 * @summary List channels
 */
export const listChannels = (
  params?: ListYtChannelsParams,
  signal?: AbortSignal,
) => {
  return customInstance<YtChannelArray>({
    url: `/channels`,
    method: 'GET',
    params,
    signal,
    baseURL: env.NEXT_PUBLIC_YOUTUBE_API_URL,
  });
};

export const getListChannelsQueryKey = (params?: ListYtChannelsParams) => {
  return [`/channels`, ...(params ? [params] : [])] as const;
};

export const getListChannelsQueryOptions = <
  TData = Awaited<ReturnType<typeof listChannels>>,
  TError = ErrorType<Error>,
>(
  params?: ListYtChannelsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listChannels>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListChannelsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listChannels>>> = ({
    signal,
  }) => listChannels(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listChannels>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListChannels<
  TData = Awaited<ReturnType<typeof listChannels>>,
  TError = ErrorType<Error>,
>(
  params?: ListYtChannelsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listChannels>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListChannelsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List channels with infinite pagination
 */
export const getListChannelsInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listChannels>>,
    ListYtChannelsParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListYtChannelsParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listChannels>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listChannels>>,
        QueryKey,
        ListYtChannelsParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListChannelsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listChannels>>,
    QueryKey,
    ListYtChannelsParams
  > = ({ signal, pageParam }) =>
    listChannels({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listChannels>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listChannels>>,
    QueryKey,
    ListYtChannelsParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListChannelsInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listChannels>>,
    ListYtChannelsParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListYtChannelsParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listChannels>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listChannels>>,
        QueryKey,
        ListYtChannelsParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListChannelsInfiniteQueryOptions(params, options);

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
